/**
 * THE BASIS — is a coverage figure a MEASUREMENT, or somebody's ESTIMATE?
 *
 * ══════════════════════════════════════════════════════════════════════════
 * WHICH OF THIS PRODUCT'S TWO COST MODELS THIS DESCRIBES
 * ══════════════════════════════════════════════════════════════════════════
 *
 * A Role carries TWO cost models that share one vocabulary, and both spell
 * `costFormula` / `revenueFormula` / `hoursFormula` on the same authoring
 * screen. This basis describes exactly one of them:
 *
 * - **COVERAGE** — `OrganizationAutomationSettings` + `RoleWorkload` + one
 *   `RoleSystemImpact` per held system, evaluated on the SERVER by
 *   `computeRoleCoverage`. **This is the model this file is about**, and the
 *   only one anything here can see: every key it reads comes out of
 *   `evaluateCoverageFormula`, which only ever runs over those three.
 * - **THE JOB MODEL** — `RoleJobType`, `RoleScopeLine`, `RoleWorkingYear`.
 *   Stored by the server, read by the server for nothing, evaluated in a
 *   BROWSER. Nothing here describes it, and a caller that renders a coverage
 *   basis beside a job-model figure is labelling one model with the other's
 *   provenance. `role-coverage-inputs-are-the-documented-three.spec.ts` pins
 *   the distinction in documentation; nothing mechanical stops that misuse.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * THIS IS NOT THE PERIOD BASIS. THEY ARE DIFFERENT AXES WITH ONE WORD.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * `coverage-period.types.ts` calls the exponent of the working year a "period
 * basis" — `0` absolute, `-1` per year — and it appears throughout this module
 * as `periodBasis`, `requiredMoneyBasis` and `WORKLOAD_WRONG_PERIOD_BASIS`.
 * That axis is about the WINDOW a magnitude is counted over. This one is about
 * WHERE THE MAGNITUDE CAME FROM. Nothing in this file reads a period basis and
 * nothing in that file reads this one; they never combine, and the two are
 * spelled `basis` and `periodBasis` on the wire so a reader can tell them
 * apart.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * THE DEFINITION
 * ══════════════════════════════════════════════════════════════════════════
 *
 * An input is MEASURED when a value in `CoverageContext.measuredInputs`
 * replaced its authored magnitude at the binding site — see `bindInput` in
 * `coverage-evaluator.ts`, which is the one place a substitution can happen and
 * therefore the one place either key set can be recorded. Every other input is
 * a number a human typed into the authoring UI.
 *
 * Over the inputs a published figure actually CONSUMED:
 *
 *     none measured        → ESTIMATED
 *     some but not all     → MIXED
 *     every one measured   → MEASURED
 *     none consumed at all → ESTIMATED   (see the vacuous-truth floor below)
 *
 * ── WHY ALL-OR-NOTHING, AND NOT A FRACTION OR A THRESHOLD ───────────────────
 *
 * The stored corpus is `mul` on every step: a coverage model is a PRODUCT of
 * its inputs. `tickets × handling time × headcount` with a measured ticket
 * count and a guessed handling time is a guess — the guess is a factor of the
 * answer, not a fraction of it, and its error is not diluted by the measured
 * terms beside it. So "mostly measured" describes nothing a reader could act
 * on, and any cut-off ("70% measured counts as MEASURED") would be a number
 * with no argument behind it that lets a guessed factor hide behind measured
 * siblings. MEASURED has to mean *no human guessed any magnitude in this
 * number*, or it means nothing a reader can rely on.
 *
 * ── THE VACUOUS-TRUTH FLOOR: NOTHING CONSUMED IS `ESTIMATED`, NEVER `MEASURED` ─
 *
 * "Every consumed input was measured" is trivially TRUE of a figure that
 * consumed none — a Role with no workload and no impacts, a row whose model
 * could not be read. Reading that as MEASURED is how a confident label gets
 * manufactured out of an absence, which is the same failure
 * `coverage-ratio.ts` refuses when it declines to report `0%` for an
 * unmodelled Role. The empty case takes the arm that cannot overstate.
 *
 * One line enforces it — the `no measured input` return — and there is
 * deliberately no second guard testing the consumed set for emptiness. Such a
 * guard READS as the floor and holds nothing up: an empty consumed set forces
 * an empty intersection, so the same line answers. It was written, measured by
 * mutation, found to change no behaviour and no test, and removed rather than
 * kept as a comforting duplicate.
 *
 * There is no fourth arm for it. A figure that does not exist has nothing to
 * label, and giving "no figure" its own basis would put a second not-modelled
 * state beside the ones `CoverageRatio` and `CoverageMoneyTerm` already carry
 * — each of which already says, in its own union, that there is no number
 * here. The basis is read BESIDE those, never instead of them.
 *
 * ── CONSUMED, NEVER DECLARED ────────────────────────────────────────────────
 *
 * A formula may declare an input it never references, and `bindInput` runs
 * only through `resolveRef` — so an unreferenced input is bound by nothing and
 * appears in neither set. Deriving the denominator from `formula.inputs`
 * instead would make a model with one unused declaration permanently
 * un-MEASURED no matter how thoroughly it was measured, and would count a
 * measurement the arithmetic never touched. Both sets come from the same
 * traversal, at the same site, so they cannot drift.
 *
 * ── ONLY THE EVALUATIONS THAT REACHED A PUBLISHED FIGURE ────────────────────
 *
 * An impact whose model evaluates cleanly and is then REFUSED — wrong
 * dimension, inadmissible window, a window disagreeing with the denominator —
 * contributes nothing to any number this engine publishes. Its keys are
 * therefore in neither set, and `coverage-ratio.ts` accumulates the basis
 * inside the same branches that do the summing so the two cannot separate.
 *
 * This is a real distinction from `measuredInputKeys`, which is published
 * beside the basis and DOES report a refused evaluation's measurements. The
 * two answer different questions and both are worth having: `measuredInputKeys`
 * is *which measurements this Role's models consumed*, and the basis is *what
 * the numbers on this screen rest on*. A Role whose only measured model was
 * refused reports a non-empty `measuredInputKeys` and a basis of ESTIMATED,
 * and both statements are true — every published figure there is hand-authored.
 * Folding the refused row in would print MIXED over a set of numbers containing
 * no measurement at all.
 *
 * ── GOING LIVE CHANGES THE BUCKET, NEVER THE BASIS ──────────────────────────
 *
 * `RoleResourceLifecycle` — `BUILDING | LIVE | RETIRED` — decides WHICH bucket a
 * system's hours land in. It has nothing to say about where those hours came
 * from: a system that is live and hand-estimated is an estimate, and a system
 * still being built whose volumes were measured is measured.
 *
 * There is no `PROPOSED`, and its absence is argued rather than an oversight:
 * `RoleResource.resourceId` must name a live row in one of five real tables, so
 * a `RoleResource` in `PROPOSED` would mean a proposed system that already
 * exists. The thing that is proposed and names no target is `RoleTask`, whose
 * existence IS the proposed state.
 *
 * TWO MECHANISMS HOLD THE RULE UP, and only the first is structural. {@link
 * deriveCoverageBasis} takes two key sets and nothing else, so a lifecycle value
 * cannot reach it without changing this signature. That alone is NOT enough,
 * because the key sets are accumulated by a CALLER: `computeRoleCoverage` could
 * perfectly well stop feeding it a non-live row's keys and the signature would
 * never move. So `coverage-lifecycle-basis.test.ts` pins the second half —
 * every basis, per row and per Role, is byte-identical across every assignment
 * of lifecycles to one fixed set of impacts.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * WHAT `MEASURED` DOES NOT CLAIM — three limits, stated because a badge on a
 * customer-facing ROI screen is read as a guarantee
 * ══════════════════════════════════════════════════════════════════════════
 *
 * 1. **It is a claim about the MAGNITUDES, never about the MODEL.** A measured
 *    binding replaces an authored number and nothing else — the dimension, the
 *    unit and the whole expression tree stay exactly as a human wrote them.
 *    That a ticket count should be MULTIPLIED by a handling time and by an
 *    attribution factor at all is an authored structural claim, and no
 *    measurement channel can confirm it. MEASURED means every number in the
 *    computation was observed; it does not mean the computation is right.
 *
 * 2. **It says nothing about the organisation's working-time model.**
 *    `hoursPerDay`, `daysPerWeek` and `workingWeeksPerYear` are an admin's
 *    assertions, and they scale every TIME quantity at binding. They are
 *    deliberately outside the basis for two reasons. They are already on the
 *    same response, per field, WITH their origin
 *    (`RoleCoverageView.workingTime`), so a reader is shown that assumption
 *    beside the number rather than being told about it a second time in a
 *    weaker form. And every model must agree on one window
 *    (`requiredBasis` in `coverage-ratio.ts`), so the annualisation factor
 *    CANCELS out of the ratio (`(I·W) / (D·W)`), out of the blended rate, and
 *    out of `savingsProjection.amount` — and money terms are `perPeriod: year`,
 *    whose factor is exactly 1. Where it does NOT cancel is the absolute
 *    totals: `workloadPersonHours`, `impactPersonHours` and each row's
 *    `personHours` are all linear in it. Folding it in would make every figure
 *    permanently MIXED, which would delete the distinction this badge exists to
 *    draw while removing nothing from the screen.
 *
 * 3. **The measurement channel is asserted, not proven.**
 *    `CoverageMeasuredInputs` is `Record<inputKey, number>`, and a number in it
 *    is a measurement because whoever assembled the context said so. Nothing
 *    here can tell an observed count from one a person typed. Today that is
 *    safe by construction rather than by trust: `coverageContextForRole` is the
 *    only door into the evaluator and no caller passes `measuredInputs`, so
 *    MEASURED is unreachable through the product and every figure it serves is
 *    ESTIMATED — which is exactly the honest answer while every input is
 *    hand-authored. **The first producer of `measuredInputs` is what makes this
 *    label true or false**, and that is where the provenance rule has to be
 *    enforced. A caller writing into that record is publishing a claim to a
 *    customer, not filling in a default.
 */

/**
 * Every basis a coverage figure can have.
 *
 * The array IS the union — the type is derived from it, so unlike the
 * unions in `coverage-enumerations.ts` there is no second declaration for
 * this one to drift from and no exhaustiveness proof to keep. That is
 * possible here only because this vocabulary has no per-arm payload; the
 * unions over there are produced by code that wants exhaustiveness checking
 * on arms it constructs, which is why they are declared as types first.
 */
export const COVERAGE_BASES = ["ESTIMATED", "MIXED", "MEASURED"] as const;

/** Whether a coverage figure rests on measurements, on estimates, or on both. */
export type CoverageBasis = (typeof COVERAGE_BASES)[number];

/** The two key sets one basis is derived from. */
export interface CoverageBasisInputs {
  /**
   * Every input key an evaluation BOUND while producing a published figure.
   *
   * The denominator of the rule. Sourced from `CoverageEvaluationOutcome`,
   * which records it at the binding site, and accumulated by the caller only
   * over evaluations whose result was actually used.
   */
  readonly consumedInputKeys: readonly string[];
  /**
   * The subset of {@link consumedInputKeys} whose authored magnitude a
   * measurement replaced.
   */
  readonly measuredInputKeys: readonly string[];
}

/**
 * Derive the basis of one figure from the keys its evaluation consumed.
 *
 * Pure, total, and dependent on nothing but the two sets — see the file header
 * for why that is the guarantee rather than an implementation detail.
 *
 * A measured key that is not in the consumed set is IGNORED rather than
 * counted. By construction it cannot happen — both sets are recorded at the
 * same binding site and accumulated in the same branch — but the rule is
 * "every input the figure consumed was measured", and a stray key is not one of
 * those. Comparing raw sizes instead would let one turn a genuinely MEASURED
 * figure into MIXED, which is the failure direction that would be believed.
 */
export function deriveCoverageBasis(inputs: CoverageBasisInputs): CoverageBasis {
  const consumed = new Set(inputs.consumedInputKeys);

  const measuredAndConsumed = new Set<string>();
  for (const key of inputs.measuredInputKeys) {
    if (consumed.has(key)) {
      measuredAndConsumed.add(key);
    }
  }

  // THIS LINE CARRIES THE VACUOUS-TRUTH FLOOR, and it is the only line that
  // does. A separate `consumed.size === 0` guard above it reads as the floor
  // and is not: an empty consumed set forces an empty intersection, so it
  // returns here anyway. Mutation-proved — deleting that guard changed no
  // behaviour and reddened no case, which is the definition of a line that is
  // not holding anything up. The floor is what the LAST line would do without
  // this one: `0 === 0` is `MEASURED`, and that is the overstatement.
  if (measuredAndConsumed.size === 0) {
    return "ESTIMATED";
  }
  return measuredAndConsumed.size === consumed.size ? "MEASURED" : "MIXED";
}
